import { FoodsIcon, FoodsTextInput } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { ProductFormProductIngredient } from "src/pages/Product/ProductForm/ProductForm.types";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { TextLink } from "src/components/Link/TextLink";
import { URLHandler } from "src/services/URLHandler";
import { canOpenLineItem } from "src/pages/Product/lineItemAccess";
import { withUsedQuantityChange } from "src/pages/Product/ProductForm/Pages/Page2/edibleQuantity";

type LineTitleProps = {
    ingredient: ProductFormProductIngredient;
    setIngredient: (newIngredient: ProductFormProductIngredient) => void;
};
export const LineTitle = (props: LineTitleProps) => {
    const { selectedCompany } = useFoodpilotContext();
    const { ingredient, setIngredient } = props;
    const theme = useTheme();

    if (selectedCompany === null) return;

    const isIntermediateProduct = ingredient.intermediateProduct !== undefined;
    const isIngredientReference = ingredient.ingredientReference !== undefined;
    const entityName =
        isIntermediateProduct ? ingredient.intermediateProduct!.title
        : isIngredientReference ? ingredient.ingredientReference!.name
        : ingredient.ingredient!.name;
    const entityUrl =
        isIntermediateProduct ?
            URLHandler.intermediateProduct.scores(selectedCompany.id, ingredient.intermediateProduct!.id)
        : isIngredientReference || !canOpenLineItem(ingredient.ingredient) ? undefined
        : URLHandler.ingredient.scores(selectedCompany.id, ingredient.ingredient!.id);

    // Concerning the `FoodsTextInput` below, I have put the `maxWidth` in order to
    // circumvent other displaying problems that were not properly adressed from before.
    // I'm thinking we'll want to make more generic way of having Tables :)
    return (
        <Box
            sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
            }}
        >
            <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                {ingredient.intermediateProduct && <FoodsIcon icon="products" size={2} />}
                {entityUrl ?
                    <TextLink url={entityUrl} text={entityName} />
                :   entityName}
            </Stack>
            <Box sx={{ gridColumn: "2", maxWidth: "150px", paddingInline: theme.spacing(2) }}>
                <FoodsTextInput
                    value={ingredient.usedQuantity?.toString() ?? ""}
                    onChange={(newValue) => {
                        const usedQuantity = newValue === "" ? null : Number(newValue);
                        setIngredient(withUsedQuantityChange(ingredient, usedQuantity));
                    }}
                    unit={"g"}
                    ExtraInputProps={{
                        type: "number",
                        placeholder: "0",
                    }}
                />
            </Box>
            <Box sx={{ gridColumn: "3", maxWidth: "150px", paddingInline: theme.spacing(2) }}>
                <FoodsTextInput
                    value={ingredient.quantity?.toString() ?? ""}
                    onChange={(newValue) => {
                        const newIngredient: ProductFormProductIngredient = {
                            ...ingredient,
                            quantity: newValue === "" ? null : Number(newValue),
                            quantityOverridden: true,
                        };
                        setIngredient(newIngredient);
                    }}
                    unit={"g"}
                    ExtraInputProps={{
                        type: "number",
                        placeholder: "0",
                    }}
                />
            </Box>
        </Box>
    );
};
